import { Component, EventEmitter, OnDestroy, OnInit, Output, ChangeDetectionStrategy, ChangeDetectorRef, inject, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  buildHeaderDataShell,
  generalConfigFromGlobal,
  normalizeFilterParams,
  rowPayloadFromActiveScreen,
} from '../../../shared/workspace-element-context';
import { Form1118Service } from './form-1118.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import * as _ from 'lodash-es';

@Component({
    selector: 'gtw-form-1118-ftc-edit',
    templateUrl: './form-1118-ftc-edit.component.html',
    styleUrls: ['./form-1118-ftc-edit.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class Form1118FtcEditComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);


  private readonly subs = new Subscription();
  @Output('request-grid-update') requestGridUpdate = new EventEmitter<any>();

  private isInitialized = false;
  private isLoading = false;

  modalTitle = 'Edit Override';
  minimise = false;
  isSaveClicked = false;
  crudLoading = false;
  showFormLoader = true;

  globalService: any;
  GENERAL_CONFIG: any;
  appService: any = {};
  baseURLs: any;
  filterParams: any = {};
  rowData: any = {};

  // Form data properties
  schedule: any;
  basket: any;
  
  // lineNo with setter to debug auto-filling
  private _lineNo: any = null;
  get lineNo(): any {
    return this._lineNo;
  }
  set lineNo(value: any) {
    this._lineNo = value;
  }
  
  // colNo with setter to debug auto-filling
  private _colNo: any = null;
  get colNo(): any {
    return this._colNo;
  }
  set colNo(value: any) {
    this._colNo = value;
  }
  
  amount: any;
  reason: string = '';
  selectedSICCode: any;
  sequenceKey: any;

  // Dropdown data
  scheduleData: any[] = [];
  basketData: any[] = [];
  lineData: any[] = [];
  colData: any[] = [];
  origColData: any[] = [];
  sicCodeList: any[] = [];

  // Flags for schedule types
  isSchdJ = false;
  isBasketGENLIM = false;
  isSchdHPI = false;

  // Operation type - default to 'U' (Edit), can be overridden by input
  private _operationType: string = 'U';
  get operationType(): string {
    return this._operationType;
  }
  isCreate = false;
  
  // Computed property for mode checks
  get isAddMode(): boolean {
    return this._operationType === 'I';
  }
  
  get isEditMode(): boolean {
    return this._operationType === 'U';
  }

  // Current year
  currentYear: any;

  // User settings
  userSettings: any;

  // Confirmation modal properties
  showModal: boolean = false;
  modalConfig: ConfirmationModalConfig = {
    title: 'Remove Override Amount',
    message: 'Are you sure to remove this Override Amount?',
    iconClass: 'fa fa-trash',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  constructor(
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private form1118Service: Form1118Service,
    private _snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.subs.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subs.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.tryInit();
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.isInitialized = false;
    this.isLoading = false;
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    const header = buildHeaderDataShell(this.gtwGlobal, this.gtwApp);
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = header.generalConfigData || {};
    this.filterParams = normalizeFilterParams(activeScreen);
    const gp = this.gtwGlobal.hydrateGlobalParamsFromContext(activeScreen, this.filterParams);
    this.globalService = { ...header, globalParams: gp };
    this.appService.USER_SETTINGS = header.userSettingsData;
    this.appService.SERVER_CONFIG = header.serverConfigData;
    this.userSettings = header.userSettingsData;
    const row = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(row)) {
      this.rowData = row.length ? row[0] : {};
    } else {
      this.rowData = row && typeof row === 'object' ? row : {};
    }

    const hasRowData = this.rowData && Object.keys(this.rowData).length > 0;
    const gridData = activeScreen?.data?.gridData;
    const isAddFromGrid = Array.isArray(gridData) && gridData.length === 0;

    const op = activeScreen?.data?.operationType || activeScreen?.data?.operation_type || '';
    if (op === 'I' || op === 'U') {
      this._operationType = op;
    } else {
      this._operationType = (isAddFromGrid || !hasRowData) ? 'I' : 'U';
    }
    this.modalTitle = this._operationType === 'I' ? 'Add Override' : 'Edit Override';
    this.isCreate = this._operationType === 'I';
  }

  onConfirm() {
    // Called from confirmation modal - proceed with delete
    this.showModal = false;
    this.performDelete();
  }

  onCancel() {
    // Called from confirmation modal - close modal
    this.showModal = false;
    this.cdr.detectChanges();
  }

  private tryInit() {
    if (this.isInitialized || this.isLoading) {
      return;
    }

    // For ADD mode, we don't need rowData
    // For EDIT mode, we need rowData with content
    const hasRequiredData = this.globalService && this.GENERAL_CONFIG && 
      (this.isAddMode || (this.rowData && Object.keys(this.rowData).length > 0));

    if (!hasRequiredData) {
      return;
    }

    this.isInitialized = true;
    this.init();
  }

  init() {
    try { 
      this.currentYear = this.filterParams.tax_year || this.globalService?.globalParams?.tax_year;

      this.checkLockStatus();

      // EDIT MODE: Load data from rowData
      if (this.isEditMode && this.rowData) {
        this.amount = this.rowData.AMOUNT;
        this.reason = this.rowData.REASON;
        this.sequenceKey = this.rowData.SEQ_KEY;
        // Load dropdown data with schedule key from rowData
        this.loadAllDropdownData();
      }
      
      // ADD MODE: Initialize with empty values and load initial dropdowns
      if (this.isAddMode) {
        this.schedule = null;
        this.basket = null;
        this.lineNo = null;
        this.colNo = null;
        this.amount = null;
        this.reason = '';
        this.selectedSICCode = null;
        this.sequenceKey = null;
        
        // Load initial dropdown options (without schedule_key)
        this.loadInitialDropdownData();
      }

      this.showFormLoader = false;
      this.cdr.detectChanges();
    } catch (e) {
      this.showFormLoader = false;
      this.cdr.detectChanges();
    }
  }

  loadInitialDropdownData() {
    this.loadDropDownData(false);
    this.loadBasketDropdownData();
    this.loadSICCodeDropdownData();
  }

  checkLockStatus() {
    const scenario = this.globalService?.globalParams?.scenario;
    const jcdKey = this.globalService?.globalParams?.jcd_key;

    this.form1118Service.loadLockStatus(
      this.GENERAL_CONFIG.base_url,
      scenario,
      jcdKey,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject && data.jsonObject !== undefined && data.jsonObject !== null) {
          if (Array.isArray(data.jsonObject) && data.jsonObject.length > 0 && data.jsonObject[0] && data.jsonObject[0].LOCK_STATUS === 'Y') {
            this._snackBar.open('Tax Return has already been filed for tax year ' + this.currentYear + '.', 'Close', {
              duration: 4000,
              panelClass: ['error-snackbar']
            });
            this.cancel();
          }
        }
      },
      (error: any) => {
        console.error('Error checking lock status:', error);
      }
    );
  }

  // ADD MODE: Load dropdown data for selected schedule (or all data if scheduleKey is null)
  loadDropDownData(isChanged: boolean) {
    const scheduleKey = this.schedule ? this.schedule.CODE_KEY : null;
    
    this.form1118Service.loadDropdownData(
      this.GENERAL_CONFIG.base_url,
      scheduleKey,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject && Array.isArray(data.jsonObject)) {
          const jsonData = data.jsonObject;
          
          // Filter data by ROW_TYPE
          this.scheduleData = _.filter(jsonData, (o: any) => o.ROW_TYPE === 'SCHD');
          this.lineData = _.filter(jsonData, (o: any) => o.ROW_TYPE === 'L');
          this.colData = _.filter(jsonData, (o: any) => o.ROW_TYPE === 'C');
          this.origColData = [...this.colData];
        } else {
          console.warn('No jsonObject in response or not an array:', data);
        }
        
        this.cdr.detectChanges();
        
      },
      (error: any) => {
        console.error('Error details:', error.message, error.status);
      }
    );
  }
  
  // ADD MODE: Load columns related to the selected Line only
  loadColumnDropDownData() {
    if (!this.lineNo) {
      return;
    }
    
    const schdName = this.lineNo.SCHD_NAME;
    
    // If schedule is NOT A or BPI, filter columns by MAP_KEY
    if (schdName !== 'F1118_A' && schdName !== 'F1118_BPI') {
      this.colData = _.filter(this.origColData, (o: any) => {
        return this.lineNo.CODE_KEY === o.MAP_KEY;
      });
    } else {
      // For A and BPI schedules, show all columns
      this.colData = [...this.origColData];
    }
    
    this.cdr.detectChanges();
  }

  // EDIT MODE: Load all dropdown data
  loadAllDropdownData() {
    if (this.isLoading) {
      return;
    }
    this.isLoading = true;

    // Load main dropdown data
    const scheduleKey = this.rowData.SCHD_KEY;

    this.form1118Service.loadDropdownData(
      this.GENERAL_CONFIG.base_url,
      scheduleKey,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        
        if (data && data.jsonObject && Array.isArray(data.jsonObject)) {
          const jsonData = data.jsonObject;

          // Filter data by ROW_TYPE
          this.scheduleData = _.filter(jsonData, (o: any) => o.ROW_TYPE === 'SCHD');
          this.lineData = _.filter(jsonData, (o: any) => o.ROW_TYPE === 'L');
          this.colData = _.filter(jsonData, (o: any) => o.ROW_TYPE === 'C');
          this.origColData = [...this.colData];

          // Set selected values from rowData - Find matching objects
          const foundSchedule = _.find(this.scheduleData, (o) => String(o.CODE_KEY) === String(this.rowData.SCHD_KEY));
          const foundLine = _.find(this.lineData, (o) => String(o.CODE_KEY) === String(this.rowData.LINE_KEY));
          
          this.schedule = foundSchedule || {
            CODE_KEY: this.rowData.SCHD_KEY,
            LINE_DESCRIPTION: this.rowData.SCHD_NAME,
            CODE_DESCRIPTION: this.rowData.SCHD_NAME
          };
          
          this.lineNo = foundLine || {
            CODE_KEY: this.rowData.LINE_KEY,
            LINE_DESCRIPTION: this.rowData.LINE_DESCRIPTION
          };
          
          // Filter columns based on line selection BEFORE setting colNo
          const firstRow: any = _.head(this.origColData);
          if (firstRow && (firstRow.SCHD_NAME !== 'F1118_A' && firstRow.SCHD_NAME !== 'F1118_BPI') && this.lineNo) {
            this.colData = _.filter(this.origColData, (o: any) => this.lineNo.CODE_KEY === o.MAP_KEY);
          }
          
          const foundCol = _.find(this.colData, (o) => String(o.CODE_KEY) === String(this.rowData.COL_KEY));
          
          this.colNo = foundCol || {
            CODE_KEY: this.rowData.COL_KEY,
            LINE_DESCRIPTION: this.rowData.COL_DESCRIPTION,
            CODE_DESCRIPTION: this.rowData.COL_DESCRIPTION
          };

          // Check schedule type flags (already done before, just update flags)
          if (firstRow) {
            const schdName = firstRow.SCHD_NAME;

            if (schdName && schdName.startsWith("F1118_JP")) {
              this.isSchdJ = true;
            }
            if (schdName && (schdName.startsWith("F1118_HP") || schdName === 'F1118_BPIII')) {
              this.isBasketGENLIM = true;
            }
            if (schdName === "F1118_HPI") {
              this.isSchdHPI = true;
            }
          }
          
          // Load SIC code data if HPI schedule (after flags are set)
          if (this.isSchdHPI) {
            this.loadSICCodeDropdownData();
          }
        }

        this.isLoading = false;
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading dropdown data:', error);
        this.isLoading = false;
        this.cdr.detectChanges();
      }
    );

    // Load basket data
    this.loadBasketDropdownData();
  }

  loadBasketDropdownData() {
    this.form1118Service.loadBasketData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        
        if (data && data.jsonObject && Array.isArray(data.jsonObject)) {
          this.basketData = data.jsonObject;

          // Set selected basket from rowData - try by BASKET_KEY first (with type conversion)
          const foundByKey = _.find(this.basketData, (o) => String(o.BASKET_KEY) === String(this.rowData.BASKET_KEY));
          const foundByDesc = _.find(this.basketData, (o) => o.BASKET_DESC === this.rowData.BASKET_DESC);
          
          this.basket = foundByKey || foundByDesc || null;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading basket data:', error);
      }
    );
  }

  loadSICCodeDropdownData() {
    this.form1118Service.loadSICCodeData(
      this.GENERAL_CONFIG.base_url,
      this.globalService,
      this.filterParams
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (data: any) => {
        if (data && data.jsonObject && Array.isArray(data.jsonObject)) {
          // Filter only displayed SIC codes
          this.sicCodeList = _.filter(data.jsonObject, (o: any) => o.IS_DISPLAY === 'Y');
          this.sicCodeList = _.orderBy(this.sicCodeList, ['CODE_DESCRIPTION']);

          // Set selected SIC code from rowData (with type conversion)
          const selectedSicCodeObj = _.find(this.sicCodeList, (o) => String(o.CODE_KEY) === String(this.rowData.SIC_CODE_KEY));
          
          this.selectedSICCode = selectedSicCodeObj || null;
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        console.error('Error loading SIC code data:', error);
      }
    );
  }

  save() {
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we save this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    // Run validation before proceeding
    const validation = this.validateForm();
    if (!validation.isValid) {
      this._snackBar.open(validation.errorMessage, 'Close', {
        duration: 4000,
        panelClass: ['error-snackbar']
      });
      return;
    }

    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Build return object
    const returnObj: any = {};
    returnObj.type = this.operationType;
    returnObj.SCHD_KEY = this.schedule.CODE_KEY;
    returnObj.SIC_CODE_KEY = this.selectedSICCode ? this.selectedSICCode.CODE_KEY : null;
    returnObj.BASKET_KEY = this.basket ? this.basket.BASKET_KEY : null;
    returnObj.LINE_KEY = this.lineNo ? this.lineNo.CODE_KEY : null;
    returnObj.COL_KEY = this.colNo ? this.colNo.CODE_KEY : null;
    returnObj.SEQ_KEY = this.sequenceKey;
    returnObj.amount = this.amount;
    returnObj.reason = this.reason;
    returnObj.FORM_NO = 'F1118';
    
    if (this.operationType === 'U' && this.rowData.object_id) {
      returnObj.object_id = this.rowData.object_id;
    }

    // Build details array
    const details = [returnObj];

    // Call save service
    this.form1118Service.saveForm1118Override(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      details,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Overrides has been successfully saved/updated', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while saving. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  deleteAmt() {
    // Prevent double-click
    if (this.crudLoading) {
      this._snackBar.open('Please wait while we process this request', 'Close', {
        duration: 4000,
        panelClass: ['info-snackbar']
      });
      return;
    }

    // Show confirmation modal before deleting
    this.showModal = true;
    this.cdr.detectChanges();
  }

  performDelete() {
    this.isSaveClicked = true;
    this.crudLoading = true;
    this.cdr.detectChanges();

    // Build return object for delete
    const returnObj: any = {};
    returnObj.type = 'D'; // Delete operation
    returnObj.SCHD_KEY = this.schedule.CODE_KEY;
    returnObj.BASKET_KEY = this.basket ? this.basket.BASKET_KEY : null;
    returnObj.LINE_KEY = this.lineNo ? this.lineNo.CODE_KEY : null;
    returnObj.COL_KEY = this.colNo ? this.colNo.CODE_KEY : null;
    returnObj.SEQ_KEY = this.sequenceKey;
    returnObj.amount = this.amount;
    returnObj.reason = this.reason;
    returnObj.FORM_NO = 'F1118';
    returnObj.object_id = this.rowData.object_id;

    // Build details array
    const details = [returnObj];

    // Call save service with delete operation
    this.form1118Service.saveForm1118Override(
      this.GENERAL_CONFIG.base_url,
      returnObj,
      details,
      this.globalService,
      this.filterParams,
      this.rowData
    ).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(
      (response: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;

        if (response.errorMessage && response.errorMessage !== 'null') {
          this._snackBar.open(response.errorMessage, 'Close', {
            duration: 4000,
            panelClass: ['error-snackbar']
          });
        } else {
          this._snackBar.open('Overrides has been deleted successfully', 'Close', {
            duration: 4000,
            panelClass: ['success-snackbar']
          });
          this.requestGridUpdate.emit('refresh');
        }
        this.cdr.detectChanges();
      },
      (error: any) => {
        this.isSaveClicked = false;
        this.crudLoading = false;
        this._snackBar.open('Error occurred while deleting. Please try again.', 'Close', {
          duration: 4000,
          panelClass: ['error-snackbar']
        });
        this.cdr.detectChanges();
      }
    );
  }

  // ADD MODE: On change of Schedule Selection
  scheduleChange() {
    if (!this.isAddMode) return; // Only for ADD mode
    
    
    // Reset dependent fields AND clear their data arrays to prevent auto-selection
    this.basket = null;
    this.lineNo = null;
    this.colNo = null;
    this.amount = null;
    this.reason = '';
    this.selectedSICCode = null;
    
    this.lineData = [];
    this.colData = [];
    this.origColData = [];
    this.sicCodeList = [];
    
    // Call cascade load
    this.getScheduleLineColAmountAndReason();
    
    this.cdr.detectChanges();
  }

  // ADD MODE: On change of Line No Selection
  lineNoChange() {
    if (!this.isAddMode) return; // Only for ADD mode
    
    
    // Reset dependent fields AND clear column data array
    this.colNo = null;
    this.amount = null;
    this.reason = '';
    
    this.colData = [];
    
    // Load Column dropdown
    this.loadColumnDropDownData();
    
    this.cdr.detectChanges();
  }

  // ADD MODE: On change of the Schedule, Line or Column -- Get their dependent dropdowns
  getScheduleLineColAmountAndReason() {
    if (!this.schedule) {
      return;
    }
    
    // Reset flags
    this.isSchdHPI = false;
    this.isSchdJ = false;
    this.isBasketGENLIM = false;
    
    const schdName = this.schedule.SCHD_NAME;
    
    // Check if Schedule is J type (JPI, JPII, JPIII, JPIV)
    if (schdName === 'F1118_JPI' || schdName === 'F1118_JPII' || 
        schdName === 'F1118_JPIII' || schdName === 'F1118_JPIV') {
      this.isSchdJ = true;
      this.isBasketGENLIM = false;
      this.basket = null;
      this.selectedSICCode = null; // Clear SIC Code for non-HPI schedules
    }
    // Check if Schedule is HPI/HPII/HPIII/BPIII type
    else if (schdName === 'F1118_HPI' || schdName === 'F1118_HPII' || 
             schdName === 'F1118_BPIII' || schdName === 'F1118_HPIII') {
      this.isSchdJ = false;
      this.isBasketGENLIM = true;
      
      // Auto-select GENLIM basket
      this.basket = _.find(this.basketData, ['BASKET_SHORT_DESC', 'GENLIM']);
      
      // Check if specifically HPI (SIC Code required)
      if (schdName === 'F1118_HPI') {
        this.isSchdHPI = true;
        this.loadSICCodeDropdownData();
      } else {
        this.selectedSICCode = null;
      }
    }
    // Normal schedule
    else {
      this.isSchdJ = false;
      this.isBasketGENLIM = false;
      this.selectedSICCode = null; // Clear SIC Code for normal schedules
    }
    
    // Load Line dropdown data
    this.loadDropDownData(true);
  }

  // Validation before save
  validateForm(): { isValid: boolean; errorMessage: string } {
    // 1. SIC Code validation (if HPI schedule)
    if (this.isSchdHPI && !this.selectedSICCode) {
      return { isValid: false, errorMessage: 'Please select the SIC CODE' };
    }
    
    // 2. Basket validation (not required for Schedule J)
    if (!this.isSchdJ && !this.basket) {
      return { isValid: false, errorMessage: 'Please select the Basket' };
    }
    
    // 3. Line validation (always required)
    if (!this.lineNo) {
      return { isValid: false, errorMessage: 'Please select the Line' };
    }
    
    // 4. Column validation (always required)
    if (!this.colNo) {
      return { isValid: false, errorMessage: 'Please select the Column' };
    }
    
    // 5. Amount validation (always required)
    if (!this.amount || this.amount.toString().length === 0) {
      return { isValid: false, errorMessage: 'Please enter the Amount' };
    }
    
    // 6. Reason is optional - no validation
    
    return { isValid: true, errorMessage: '' };
  }

  reset() {
    if (this.isAddMode) {
      // ADD MODE: Clear all fields
      this.amount = '';
      this.reason = '';
      this.schedule = null;
      this.lineNo = null;
      this.colNo = null;
      this.basket = null;
      this.sequenceKey = null;
      this.selectedSICCode = null;
      
      // Reset flags
      this.isSchdJ = false;
      this.isBasketGENLIM = false;
      this.isSchdHPI = false;
      
    } else {
      // EDIT MODE: Reset form to original values from rowData
      this.amount = this.rowData.AMOUNT;
      this.reason = this.rowData.REASON;
      
      // Reload all dropdown data to restore original selections
      this.loadAllDropdownData();
      
    }
    
    this.cdr.detectChanges();
  }

  cancel() {
    this.requestGridUpdate.emit('cancel');
  }

  onAmountKeydown(event: KeyboardEvent) {
    const key = event.key;
    const currentValue = (event.target as HTMLInputElement).value;
    const cursorPosition = (event.target as HTMLInputElement).selectionStart || 0;

    // Allow: backspace, delete, tab, escape, enter, arrows, home, end
    if (
      key === 'Backspace' || 
      key === 'Delete' || 
      key === 'Tab' || 
      key === 'Escape' || 
      key === 'Enter' ||
      key === 'ArrowLeft' || 
      key === 'ArrowRight' || 
      key === 'ArrowUp' || 
      key === 'ArrowDown' ||
      key === 'Home' ||
      key === 'End'
    ) {
      return; // Allow
    }

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+Z
    if (event.ctrlKey || event.metaKey) {
      return; // Allow
    }

    // Allow: minus (-) only at the beginning and if not already present
    if (key === '-') {
      if (cursorPosition === 0 && !currentValue.includes('-')) {
        return; // Allow
      }
      event.preventDefault(); // Block
      return;
    }

    // Allow: decimal point (.) only once
    if (key === '.' || key === ',') {
      if (!currentValue.includes('.') && !currentValue.includes(',')) {
        return; // Allow
      }
      event.preventDefault(); // Block
      return;
    }

    // Allow: numbers 0-9
    if (key >= '0' && key <= '9') {
      return; // Allow
    }

    // Block all other keys (including 'e', 'E', '+', etc.)
    event.preventDefault();
  }

  // Compare function for dropdown objects
  compareByCodeKey(o1: any, o2: any): boolean {
    if (!o1 || !o2) {
      return o1 === o2;
    }
    // Convert to same type for comparison (handle string vs number)
    const key1 = String(o1.CODE_KEY);
    const key2 = String(o2.CODE_KEY);
    const result = key1 === key2;
    return result;
  }

  // Compare function for basket objects
  compareByBasketKey(o1: any, o2: any): boolean {
    if (!o1 || !o2) {
      return o1 === o2;
    }
    // Convert to same type for comparison (handle string vs number)
    const key1 = String(o1.BASKET_KEY);
    const key2 = String(o2.BASKET_KEY);
    const result = key1 === key2;
    return result;
  }
}

